import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useCartStore } from '@/store/cartStore'
import { FaTimes, FaShoppingCart, FaTrash, FaPlus, FaMinus } from 'react-icons/fa'
import { formatCurrency } from '@/utils/helpers'
import toast from 'react-hot-toast'

interface CartSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const CartSidebar = ({ isOpen, onClose }: CartSidebarProps) => {
  const { t } = useTranslation()
  const { items, removeItem, updateQuantity, getTotalPrice, clearCart } = useCartStore()
  const totalPrice = getTotalPrice()
  const shippingCost = 5.99
  const finalTotal = totalPrice + shippingCost

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-dark-900 border-l border-dark-700 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-dark-700">
              <div className="flex items-center gap-3">
                <FaShoppingCart className="w-5 h-5 text-primary-400" />
                <h2 className="text-xl font-bold text-gray-100">{t('cart.title')}</h2>
                {items.length > 0 && (
                  <span className="px-2 py-1 bg-primary-500 text-white text-xs rounded-full">
                    {items.length}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-300 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <FaShoppingCart className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-400 mb-4">{t('cart.empty')}</p>
                  <Link
                    to="/shop"
                    onClick={onClose}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors"
                  >
                    {t('cart.continueShopping')}
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <motion.div
                      key={item.productId}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-dark-800 rounded-lg p-4 border border-dark-700"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-gray-100 font-semibold mb-1">{item.productName}</h3>
                          <p className="text-primary-400 font-bold">{formatCurrency(item.price)}</p>
                        </div>
                        <button
                          onClick={() => {
                            removeItem(item.productId)
                            toast.success(t('cart.remove') || 'Entfernt')
                          }}
                          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-700">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            className="p-1.5 bg-dark-700 hover:bg-dark-600 rounded text-gray-300 transition-colors"
                          >
                            <FaMinus className="w-3 h-3" />
                          </button>
                          <span className="text-gray-100 font-semibold w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            className="p-1.5 bg-dark-700 hover:bg-dark-600 rounded text-gray-300 transition-colors"
                          >
                            <FaPlus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-gray-100 font-bold">
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-dark-700 p-4 sm:p-6 bg-dark-800/50">
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t('cart.subtotal')}</span>
                    <span className="text-gray-200">{totalPrice.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t('cart.shipping')}</span>
                    <span className="text-gray-200">{shippingCost.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-dark-700">
                    <span className="text-gray-100">{t('cart.total')}</span>
                    <span className="text-primary-400">{finalTotal.toFixed(2)} €</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Link
                    to="/checkout"
                    onClick={onClose}
                    className="block w-full py-3 px-4 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-primary-500/50 text-center"
                  >
                    {t('cart.checkout')}
                  </Link>
                  <button
                    onClick={() => {
                      clearCart()
                      toast.success(t('cart.cleared') || 'Warenkorb geleert')
                    }}
                    className="w-full py-2 px-4 text-gray-400 hover:text-red-400 transition-colors text-sm"
                  >
                    {t('cart.clearAll') || 'Warenkorb leeren'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CartSidebar
